import {
  REFERRAL_WINDOW_DAYS,
  deriveAiAssistant,
  deriveTrafficSource,
  isAiAssistant,
  isTrafficSource,
  normalizeHandle,
  sanitizeAdCampaign,
} from "@majourneys/shared";

const COOKIE = "mj_src";
/** Which chat assistant, written with `mj_src` when the channel is `ai_assistant`. */
const AI_COOKIE = "mj_src_ai";
/**
 * First-touch `utm_campaign`, written with `mj_src` and never on its own. Our
 * Google Ads final URL suffix sets it to the numeric campaign id, so a booking
 * can be credited to the campaign that brought the guest.
 */
const CAMPAIGN_COOKIE = "mj_cmp";
/** Matches the Google Ads attribution window the gclid cookie uses. */
const MAX_AGE_DAYS = 90;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const hit = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

function writeCookie(name: string, value: string, days = MAX_AGE_DAYS): void {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * The Google Ads click id from the landing URL, held in memory only.
 *
 * Since the cookie banner (2026-09-30) gtag runs in Consent Mode and writes no
 * `_gcl_aw` cookie until the visitor taps "Accept". Someone who lands from an
 * ad, browses, and accepts a few pages later has lost `?gclid=` from the
 * address by then, so the booking went through with no click id. Keeping it in
 * a variable stores nothing on the device; it lives as long as the tab and is
 * only handed to a booking once consent is granted (see `landingGclid`).
 */
let landingClickId: string | null = null;
/** Meta's `?fbclid=` from the landing URL and when we saw it, held in memory like the gclid. */
let landingFbclid: { id: string; at: number } | null = null;
let adConsentGranted: () => boolean = () => false;

/**
 * How the host app tells this package whether the visitor accepted ad
 * cookies. Apps that never register a check never release the held click id.
 */
export function setAdConsentCheck(check: () => boolean): void {
  adConsentGranted = check;
}

/** The landing click id, but only for a visitor who has accepted. */
export function landingGclid(): string | undefined {
  return landingClickId && adConsentGranted() ? landingClickId : undefined;
}

/**
 * Record how this visitor first reached us, once.
 *
 * Called on the first page they land on. The referrer and any `?utm_source=`
 * only exist on that page — the router drops the query string and checkout is
 * several navigations later — so the channel is parked in a cookie the way the
 * Google Ads click id is. First touch wins: someone who arrives from Instagram,
 * leaves, and comes back directly still counts as Instagram, and an internal
 * navigation never overwrites anything.
 *
 * Returns the channel in force after the call, or null when there is nothing to
 * record (server-side render, or an internal referrer with no cookie yet).
 */
export function captureFirstTouch(): string | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  // Before the first-touch early return: a returning visitor who clicks a new
  // ad still brings a fresh click id. Same length cap as the API.
  const gclid = params.get("gclid")?.trim();
  if (gclid && gclid.length <= 512) landingClickId = gclid;
  const fbclid = params.get("fbclid")?.trim();
  if (fbclid && fbclid.length <= 400) landingFbclid = { id: fbclid, at: Date.now() };
  // Also before the early return, and last click wins: the campaign answers
  // "which ad brought this booking", so a returning visitor who clicks an ad
  // must be credited to it even though their channel stays first touch.
  const campaign = sanitizeAdCampaign(params.get("utm_campaign"));
  if (campaign) writeCookie(CAMPAIGN_COOKIE, campaign);

  const signals = {
    referrer: document.referrer,
    utmSource: params.get("utm_source"),
    utmMedium: params.get("utm_medium"),
    userAgent: navigator.userAgent,
    gclid: params.get("gclid"),
    self: window.location.hostname,
  };

  const existing = readCookie(COOKIE);
  if (existing && isTrafficSource(existing)) {
    // Visitors recorded before we kept the assistant's name: fill it in the
    // next time they arrive from one, without touching the first-touch channel.
    if (existing === "ai_assistant" && !readCookie(AI_COOKIE)) {
      const assistant = deriveAiAssistant(signals);
      if (assistant) writeCookie(AI_COOKIE, assistant);
    }
    return existing;
  }

  const source = deriveTrafficSource(signals);
  if (!source) return null;

  writeCookie(COOKIE, source);
  const assistant = deriveAiAssistant(signals);
  if (assistant) writeCookie(AI_COOKIE, assistant);
  return source;
}

/** The stored first-touch `utm_campaign`, or undefined when there was none. */
export function readAdCampaign(): string | undefined {
  return sanitizeAdCampaign(readCookie(CAMPAIGN_COOKIE)) ?? undefined;
}

/** The stored first-touch channel, or undefined when we never recorded one. */
export function readTrafficSource(): string | undefined {
  const value = readCookie(COOKIE);
  return value && isTrafficSource(value) ? value : undefined;
}

/** The assistant behind an `ai_assistant` first touch, or undefined. */
export function readAiAssistant(): string | undefined {
  if (readTrafficSource() !== "ai_assistant") return undefined;
  const value = readCookie(AI_COOKIE);
  return value && isAiAssistant(value) ? value : undefined;
}

/**
 * What a booking tells the API about ad consent and the visitor's Meta ids.
 *
 * Empty unless the visitor accepted ad cookies, so a guest who declined (or
 * never answered) books with no consent flag and nothing Meta can match on.
 * With consent: `_fbp` is the pixel's browser id; `_fbc` is the pixel's click
 * id cookie, or, when the pixel had not loaded yet on the landing page, the
 * same `fb.1.<ms>.<fbclid>` value built from the click id held in memory.
 * The API uses these to send the booking's lead stages to Meta.
 */
export function readMetaAttribution(): { adConsent?: true; metaFbp?: string; metaFbc?: string } {
  if (typeof document === "undefined" || !adConsentGranted()) return {};
  const fbp = readCookie("_fbp")?.trim();
  const fbcCookie = readCookie("_fbc")?.trim();
  const fbc = fbcCookie || (landingFbclid ? `fb.1.${landingFbclid.at}.${landingFbclid.id}` : undefined);
  return {
    adConsent: true,
    ...(fbp && fbp.length <= 512 ? { metaFbp: fbp } : {}),
    ...(fbc && fbc.length <= 512 ? { metaFbc: fbc } : {}),
  };
}

const REF_COOKIE = "mj_ref";

/**
 * Ambassador attribution (owner, 2026-10-09): a `?ref=<handle>` link, or a
 * visit to an ambassador's page, credits that ambassador for 30 days, and
 * the LAST one clicked wins (unlike `mj_src`, which is first touch). The
 * value is only a handle; the API decides whether it counts. Not an ad
 * cookie: it pays a partner and tracks nobody, so it does not wait for
 * consent, the same as `mj_src`.
 */
export function setReferral(handle: string): string | null {
  const clean = normalizeHandle(handle);
  if (!clean) return readReferral() ?? null;
  writeCookie(REF_COOKIE, clean, REFERRAL_WINDOW_DAYS);
  return clean;
}

/**
 * Counts one click for this ambassador, at most once per tab: a refresh or
 * a second visit to the same page in the tab is not a second click. A
 * beacon, so it never slows the page; a blocked beacon only loses a count.
 * `path` is the page the link landed on: when it is a listing, the API also
 * counts the click for that listing ("which link works"). Still once per
 * handle per tab, so the total means what it always did.
 */
export function countReferralClick(apiBase: string | undefined, handle: string, path?: string): boolean {
  const clean = normalizeHandle(handle);
  if (!clean || !apiBase || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return false;
  }
  const key = `mj_ref_click:${clean}`;
  try {
    if (window.sessionStorage.getItem(key)) return false;
  } catch {
    // No sessionStorage (private mode): count it, as before.
  }
  try {
    const sent = navigator.sendBeacon(
      `${apiBase}/ambassadors/click`,
      new Blob([JSON.stringify(path ? { handle: clean, path: path.slice(0, 400) } : { handle: clean })], {
        type: "application/json",
      }),
    );
    if (sent) {
      try {
        window.sessionStorage.setItem(key, "1");
      } catch {
        /* see above */
      }
    }
    return sent;
  } catch {
    return false;
  }
}

/**
 * Called on every landing: a valid `?ref=` refreshes the cookie and counts
 * the click (once per tab, see `countReferralClick`). Junk is ignored and
 * the handle already stored stays in force. Returns the handle in force.
 */
export function captureReferral(apiBase?: string): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("ref");
  const clean = normalizeHandle(raw);
  if (!clean) return readReferral() ?? null;
  setReferral(clean);
  countReferralClick(apiBase, clean, window.location.pathname);
  return clean;
}

/** The ambassador handle in force, or undefined. Sent with every checkout. */
export function readReferral(): string | undefined {
  return normalizeHandle(readCookie(REF_COOKIE)) ?? undefined;
}
